import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useIsMutating } from 'react-query';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { IncomeToDelete, IncomeToUpsert } from '../../Income.meta';
import { IncomeType } from '../../api/useIncomeQuery';
import { IncomeData } from '../../useIncome';
import { UPSERT_INCOME_FORM_ID } from './UpsertIncomeModal.meta';
import { BusinessForm } from '../../forms/Business/BusinessForm';
import { UPSERT_INCOME_MK } from '../../api/useUpsertIncomeMutation';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { RealEstateForm } from '../../forms/RealEstate/RealEstateForm';
import { EmploymentForm } from '../../forms/Employment/EmploymentForm';
import { SocialSecurityForm } from '../../forms/SocialSecurity/SocialSecurityForm';
import { OtherIncomeForm } from '../../forms/OtherIncome/OtherIncomeForm';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

const FormsDict = {
  [IncomeType.Salary]: EmploymentForm,
  [IncomeType.Business]: BusinessForm,
  [IncomeType.RealEstate]: RealEstateForm,
  [IncomeType.SocialSecurity]: SocialSecurityForm,
  [IncomeType.Other]: OtherIncomeForm,
};

export interface Props {
  clientId: string;
  income: IncomeToUpsert;
  data: IncomeData;
  onIncomeTypeChange(incomeType: IncomeType): void;
  onSuccess(): void;
  onClose(): void;
  incomeToDelete: IncomeToDelete | null;
  setIncomeToDelete(incomeToDelete: IncomeToDelete | null): void;
  workflowProps?: WorkflowComponentProps;
}

export const UpsertIncomeModal = ({
  clientId,
  income,
  data,
  onIncomeTypeChange,
  onSuccess,
  onClose,
  incomeToDelete,
  setIncomeToDelete,
  workflowProps,
}: Props) => {
  const {
    Options: { IncomeTypeOptions, BusinessAssetOption, RealEstateAssetOptions },
  } = data;

  const { Dob: primaryDob } = useSelector(selectCurrentClient);

  const isUpsertingIncome = useIsMutating([UPSERT_INCOME_MK]) > 0;

  const initialIncomeType = useRef(income.IncomeType).current;

  const incomeTitle =
    IncomeTypeOptions.find(option => option.value === income.IncomeType)?.label || 'Income';

  const Form =
    income.IncomeType !== null && income.IncomeType !== undefined
      ? FormsDict[income.IncomeType]
      : React.Fragment;

  const getShowFooter = () => {
    if (income.IncomeType === null || income.IncomeType === undefined) {
      return false;
    }

    if (income.IncomeType === IncomeType.Business && BusinessAssetOption.length === 0) {
      return false;
    }

    if (income.IncomeType === IncomeType.RealEstate && RealEstateAssetOptions.length === 0) {
      return false;
    }

    if (income.IncomeType === IncomeType.SocialSecurity && !primaryDob) {
      return false;
    }

    return true;
  };

  return (
    <Modal
      isOpen
      isHidden={incomeToDelete !== null}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'income--upsert-income--modal-header' }}
    >
      <ModalHeader as="h2" id="income--upsert-income--modal-header">
        {initialIncomeType !== undefined
          ? `${income.Id ? 'Edit' : 'Add'} ${incomeTitle}`
          : 'Add Income'}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody css="min-height: 30vh">
        {initialIncomeType === undefined && (
          <Select
            label="Income Type"
            placeholder="Select Income Type"
            value={IncomeTypeOptions.find(({ value }) => value === income.IncomeType)}
            options={IncomeTypeOptions}
            onChange={opt => {
              if (opt) {
                onIncomeTypeChange(opt.value);
              }
            }}
            isPortal
            mb="16"
          />
        )}

        <Form
          clientId={clientId}
          data={data}
          income={income.Id ? income : null}
          onSuccess={onSuccess}
          workflowProps={workflowProps}
        />
      </ModalBody>

      {getShowFooter() && (
        <ModalFooter>
          {income.Id && (
            <Button
              variant="errorSecondary"
              onClick={() => {
                if (income.Id) {
                  setIncomeToDelete({ id: income.Id, name: income.Name || '' });
                }
              }}
              mr="auto"
            >
              Delete
            </Button>
          )}

          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            form={UPSERT_INCOME_FORM_ID}
            isLoading={isUpsertingIncome}
            ml="8"
          >
            {income.Id ? 'Save' : 'Add Income'}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};
